import { useCallback, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, ApiError } from "@/lib/api";
import { useAuthToken } from "@/lib/auth";
import { failureView, type FailureFields } from "@/lib/failureClass";

/** Failure verdict + bundle of a failed job (detail page): the class chip,
 *  the one-line cause, and the failure bundle — the FULL build output, guest
 *  console, guest memory/kernel state, exact command lines and environment
 *  (secrets scrubbed), uploaded by the runner at failure time. Reached only
 *  through the SSO-gated broker routes /v1/admin/jobs/:id/bundle[/content].
 *  Self-contained: it renders nothing for a job with no verdict. */
export function FailurePanel({
  jobId,
  job,
}: {
  jobId: string;
  job: FailureFields;
}) {
  const token = useAuthToken();
  const v = failureView(job);
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState<"view" | "download" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const base = `/admin/jobs/${encodeURIComponent(jobId)}/bundle`;
  const errMsg = (e: unknown, d: string) =>
    e instanceof ApiError ? e.message : d;

  const view = useCallback(() => {
    setBusy("view");
    setError(null);
    api<string>(token, `${base}/content`)
      .then(setText)
      .catch((e) => setError(errMsg(e, "failed to load the bundle")))
      .finally(() => setBusy(null));
  }, [token, base]);

  const download = useCallback(() => {
    setBusy("download");
    setError(null);
    api<{ ok: true; url: string }>(token, base)
      .then((r) => window.open(r.url, "_blank", "noopener,noreferrer"))
      .catch((e) => setError(errMsg(e, "failed to get the download link")))
      .finally(() => setBusy(null));
  }, [token, base]);

  if (!v) return null;
  return (
    <div
      className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-2"
      data-testid="failure-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs font-semibold text-destructive">Failure</div>
        <Badge variant={v.tone} title={v.title} className="cursor-help">
          {v.label}
        </Badge>
        {v.backfilled && (
          <span className="text-xs text-muted-foreground">
            classified now from the stored log
          </span>
        )}
      </div>
      {v.cause && (
        <div className="text-sm font-mono break-words">{v.cause}</div>
      )}
      {v.hasBundle ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" disabled={busy !== null} onClick={view}>
            {busy === "view"
              ? "loading…"
              : text
                ? "reload bundle"
                : "view failure bundle"}
          </Button>
          <Button
            variant="secondary"
            disabled={busy !== null}
            onClick={download}
          >
            {busy === "download" ? "preparing…" : "download bundle (.txt.gz)"}
          </Button>
          <span className="text-xs text-muted-foreground">
            full build output, guest console / memory / kernel log, command
            lines and environment (secrets scrubbed); kept 30 days
          </span>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No failure bundle for this job (it failed before bundles existed,
          never reached a VM, or the upload failed).
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
      {text !== null && (
        <pre className="bg-muted rounded-md p-3 text-xs overflow-auto max-h-[32rem] whitespace-pre-wrap">
          {text}
        </pre>
      )}
    </div>
  );
}
